import { supabase } from '../lib/supabase';
import type { PlayerGoal } from '../types/playerCard';

/**
 * Service for managing Player Goals
 * Handles development goals and tracking
 */
class PlayerGoalService {
  /**
   * Get all goals for a player card
   */
  async getGoals(cardId: string): Promise<PlayerGoal[]> {
    const { data, error } = await supabase
      .from('player_goals')
      .select('*')
      .eq('player_card_id', cardId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[PlayerGoalService] Error fetching goals:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get a specific goal
   */
  async getGoal(goalId: string): Promise<PlayerGoal | null> {
    const { data, error } = await supabase
      .from('player_goals')
      .select('*')
      .eq('id', goalId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      console.error('[PlayerGoalService] Error fetching goal:', error);
      throw error;
    }

    return data;
  }

  /**
   * Create a new goal
   */
  async createGoal(
    cardId: string,
    goal: {
      goal_text: string;
      category: 'skill' | 'physical' | 'mental' | 'team';
      target_date?: string;
      notes?: string;
    }
  ): Promise<PlayerGoal> {
    const { data, error } = await supabase
      .from('player_goals')
      .insert({
        player_card_id: cardId,
        goal_text: goal.goal_text,
        category: goal.category,
        target_date: goal.target_date || null,
        status: 'not_started',
        notes: goal.notes || null,
      })
      .select()
      .single();

    if (error) {
      console.error('[PlayerGoalService] Error creating goal:', error);
      throw error;
    }

    return data;
  }

  /**
   * Update a goal
   */
  async updateGoal(
    goalId: string,
    updates: Partial<PlayerGoal>
  ): Promise<PlayerGoal> {
    const { data, error } = await supabase
      .from('player_goals')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', goalId)
      .select()
      .single();

    if (error) {
      console.error('[PlayerGoalService] Error updating goal:', error);
      throw error;
    }

    return data;
  }

  /**
   * Update goal status
   */
  async updateGoalStatus(
    goalId: string,
    status: 'not_started' | 'in_progress' | 'achieved' | 'cancelled'
  ): Promise<PlayerGoal> {
    return this.updateGoal(goalId, { status });
  }

  /**
   * Delete a goal
   */
  async deleteGoal(goalId: string): Promise<void> {
    const { error } = await supabase
      .from('player_goals')
      .delete()
      .eq('id', goalId);

    if (error) {
      console.error('[PlayerGoalService] Error deleting goal:', error);
      throw error;
    }
  }

  /**
   * Get goals by status
   */
  async getGoalsByStatus(
    cardId: string,
    status: 'not_started' | 'in_progress' | 'achieved' | 'cancelled'
  ): Promise<PlayerGoal[]> {
    const { data, error } = await supabase
      .from('player_goals')
      .select('*')
      .eq('player_card_id', cardId)
      .eq('status', status)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[PlayerGoalService] Error fetching goals by status:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get goals by category
   */
  async getGoalsByCategory(
    cardId: string,
    category: 'skill' | 'physical' | 'mental' | 'team'
  ): Promise<PlayerGoal[]> {
    const { data, error } = await supabase
      .from('player_goals')
      .select('*')
      .eq('player_card_id', cardId)
      .eq('category', category)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[PlayerGoalService] Error fetching goals by category:', error);
      throw error;
    }

    return data || [];
  }
}

export const playerGoalService = new PlayerGoalService();

